/**
 * apiService.js
 * =============
 * Thin wrapper around the Vercel serverless API.
 * Automatically injects the current user's Firebase ID token.
 *
 * All functions throw on error (response.ok === false → throw with error message).
 */

import { auth } from '../firebase';

const API_BASE = '/api';  // relative — works on both localhost and wohler.ai

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiFetch(path, options = {}) {
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `API error ${res.status}`);
  return data;
}

/** List all users with roles */
export const listUsers = () => apiFetch('/users');

/** Invite a new user (or update role of existing user) */
export const inviteUser = ({ email, displayName, role }) =>
  apiFetch('/invite', { method: 'POST', body: { email, displayName, role } });

/** Update a user's role */
export const updateUserRole = (uid, role) =>
  apiFetch('/users', { method: 'PATCH', body: { uid, role } });

/** Delete a user */
export const deleteUser = (uid) =>
  apiFetch('/users', { method: 'DELETE', body: { uid } });

/** Re-generate a password reset / invite link */
export const getResetLink = (uid) =>
  apiFetch('/reset-link', { method: 'POST', body: { uid } });
